/**
 * AdminQuarantineGrid - AG Grid implementation for Quarantine Management
 *
 * Displays quarantined/scanned files in a sortable, filterable grid with:
 * - File name, Client, Status, Reason, Scan Details
 * - Read-only (no edit/delete actions)
 */

import React, { useMemo } from "react";
import styled from "styled-components";
import { AgGridReact } from "ag-grid-react";
import {
  ModuleRegistry,
  AllCommunityModule,
  themeQuartz,
} from "ag-grid-community";
import type { ColDef, ICellRendererParams } from "ag-grid-community";
import { QuarantineStatus } from "../../../generated/graphql";

// Register AG Grid v34 modules
ModuleRegistry.registerModules([AllCommunityModule]);

// Configure AG Grid v34 theme
const gridTheme = themeQuartz;

/**
 * Grid wrapper with fixed height
 */
const GridWrapper = styled.div`
  width: 100%;
  height: 600px;
  margin-top: 20px;
`;

/**
 * Status badge with color coding
 */
const StatusBadge = styled.span<{ status: QuarantineStatus }>`
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
  color: white;
  background-color: ${(props) => {
    switch (props.status) {
      case QuarantineStatus.Clean:
        return "#28a745";
      case QuarantineStatus.Quarantined:
        return "#dc3545";
      case QuarantineStatus.ScanFailed:
        return "#fd7e14";
      default:
        return "#6c757d";
    }
  }};
`;

/**
 * File details container
 */
const FileDetails = styled.div`
  font-size: 12px;
  color: #666;
  margin-top: 4px;
`;

/**
 * Client details container
 */
const ClientDetails = styled.div`
  font-size: 12px;
  color: #666;
  margin-top: 2px;
`;

/**
 * Scan details container
 */
const ScanDetails = styled.div`
  font-size: 12px;
  color: #666;
`;

/**
 * Quarantine file row interface
 */
export interface QuarantineFileRow {
  id: string;
  fileName: string;
  extension: string;
  createdAt: string;
  scannedAt?: string | null;
  quarantineStatus: QuarantineStatus;
  quarantineReason?: string | null;
  scanEngine?: string | null;
  scanResult?: string | null;
  client?: {
    clientId: string;
    client_name: string;
    folder: string;
  } | null;
}

/**
 * Props for AdminQuarantineGrid
 */
interface AdminQuarantineGridProps {
  rows: ReadonlyArray<QuarantineFileRow>;
}

/**
 * File cell renderer - displays file name with extension and dates
 */
const FileCellRenderer: React.FC<ICellRendererParams<QuarantineFileRow>> = (
  params,
) => {
  const { data } = params;
  if (!data) return null;

  return (
    <div>
      <strong>{data.fileName}</strong>
      <FileDetails>
        Type: {data.extension.toUpperCase()} • Uploaded:{" "}
        {new Date(data.createdAt).toLocaleDateString()}
        {data.scannedAt && (
          <> • Scanned: {new Date(data.scannedAt).toLocaleDateString()}</>
        )}
      </FileDetails>
    </div>
  );
};

/**
 * Client cell renderer - displays client name and folder
 */
const ClientCellRenderer: React.FC<ICellRendererParams<QuarantineFileRow>> = (
  params,
) => {
  const { data } = params;
  if (!data) return null;

  if (!data.client) return <span style={{ color: "#999" }}>—</span>;

  return (
    <div>
      <strong>{data.client.client_name}</strong>
      <ClientDetails>Folder: {data.client.folder}</ClientDetails>
    </div>
  );
};

/**
 * Status cell renderer - displays colored badge
 */
const StatusCellRenderer: React.FC<ICellRendererParams<QuarantineFileRow>> = (
  params,
) => {
  const { data } = params;
  if (!data) return null;

  return (
    <StatusBadge status={data.quarantineStatus}>
      {data.quarantineStatus}
    </StatusBadge>
  );
};

/**
 * Reason cell renderer - displays quarantine reason
 */
const ReasonCellRenderer: React.FC<ICellRendererParams<QuarantineFileRow>> = (
  params,
) => {
  const { data } = params;
  if (!data) return null;

  return (
    <div style={{ fontSize: "12px", color: "#666", wordWrap: "break-word" }}>
      {data.quarantineReason || "No reason provided"}
    </div>
  );
};

/**
 * Scan details cell renderer
 */
const ScanDetailsCellRenderer: React.FC<
  ICellRendererParams<QuarantineFileRow>
> = (params) => {
  const { data } = params;
  if (!data) return null;

  if (!data.scanEngine) {
    return <span style={{ color: "#999" }}>No scan data</span>;
  }

  return (
    <ScanDetails>
      <div>
        <strong>Engine:</strong> {data.scanEngine}
      </div>
      {data.scanResult && (
        <div>
          <strong>Result:</strong> {data.scanResult}
        </div>
      )}
    </ScanDetails>
  );
};

/**
 * AdminQuarantineGrid Component
 */
const AdminQuarantineGrid: React.FC<AdminQuarantineGridProps> = ({ rows }) => {
  const columnDefs = useMemo<ColDef<QuarantineFileRow>[]>(
    () => [
      {
        headerName: "File",
        field: "fileName",
        cellRenderer: FileCellRenderer,
        flex: 2,
        minWidth: 250,
        suppressHeaderMenuButton: true,
      },
      {
        headerName: "Client",
        cellRenderer: ClientCellRenderer,
        flex: 1,
        minWidth: 180,
        suppressHeaderMenuButton: true,
      },
      {
        headerName: "Status",
        field: "quarantineStatus",
        cellRenderer: StatusCellRenderer,
        width: 140,
        filter: true,
        suppressHeaderMenuButton: true,
      },
      {
        headerName: "Reason",
        field: "quarantineReason",
        cellRenderer: ReasonCellRenderer,
        flex: 1,
        minWidth: 200,
        suppressHeaderMenuButton: true,
      },
      {
        headerName: "Scan Details",
        cellRenderer: ScanDetailsCellRenderer,
        flex: 1,
        minWidth: 180,
        sortable: false,
        filter: false,
        suppressHeaderMenuButton: true,
      },
    ],
    [],
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      suppressHeaderMenuButton: true,
    }),
    [],
  );

  return (
    <GridWrapper data-testid="quarantine-grid">
      <AgGridReact<QuarantineFileRow>
        theme={gridTheme}
        rowData={rows as QuarantineFileRow[]}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        getRowId={(params) => params.data.id}
        suppressCellFocus={false}
      />
    </GridWrapper>
  );
};

export default AdminQuarantineGrid;
